import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { LoggedOutHomeImage } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { BrandInfo } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/useGetBrandValues";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { cn } from "@faire/web--source/tools/cn";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";

const MOBILE_BANNER_SIZE = 311;
const TABLET_BANNER_HEIGHT = 413;
const TABLET_BANNER_WIDTH = 440;
const DESKTOP_BANNER_SIZE = 455;
const XL_DESKTOP_BANNER_HEIGHT = 500;
const XL_DESKTOP_BANNER_WIDTH = 640;
const XXL_DESKTOP_BANNER_HEIGHT = 635;
const XXL_DESKTOP_BANNER_WIDTH = 824;

export const BrandValueBannerBrandInfo: React.FC<{
  brandInfo: BrandInfo | undefined;
  className?: string;
}> = ({ brandInfo, className }) => {
  if (!brandInfo) {
    return null;
  }
  return (
    <Flex
      className={cn(
        "w-full flex-col gap-6 pt-10 pb-12 md:flex-row md:gap-8 lg:gap-14",
        className
      )}
    >
      <LoggedOutHomeImage
        lazyLoad
        className="relative size-full md:h-[413px] md:w-[440px] lg:size-[455px] xl:h-[500px] xl:w-[640px] 2xl:h-[635px] 2xl:w-[824px]"
        responsiveImage={{
          mobileAndAbove: IImage.build({ url: brandInfo.image }),
        }}
        optimizeImageOptions={{
          mobileAndAbove: {
            width: MOBILE_BANNER_SIZE,
            height: MOBILE_BANNER_SIZE,
          },
          tabletAndAbove: {
            width: TABLET_BANNER_WIDTH,
            height: TABLET_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
          desktopAndAbove: {
            width: DESKTOP_BANNER_SIZE,
            height: DESKTOP_BANNER_SIZE,
          },
          xLargeAndAbove: {
            width: XL_DESKTOP_BANNER_WIDTH,
            height: XL_DESKTOP_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
          xxLargeDesktop: {
            width: XXL_DESKTOP_BANNER_WIDTH,
            height: XXL_DESKTOP_BANNER_HEIGHT,
            resizeMethod: "fill",
          },
        }}
      />
      <RightColumn brandInfo={brandInfo} />
    </Flex>
  );
};

const RightColumn: React.FC<{
  brandInfo: BrandInfo;
}> = ({ brandInfo }) => {
  const { name, location, description, cta, token } = brandInfo;
  return (
    <Column className="relative pt-2 md:h-[413px] md:flex-1 lg:h-[455px] lg:pr-10 xl:h-[500px] xl:pr-20 2xl:h-[635px] 2xl:pr-[200px]">
      <Typography
        variant="subheadingSansMedium"
        className="text-sm tracking-[0.15px] lg:text-lg lg:leading-[26px] xl:text-[22px] xl:leading-8 2xl:text-3xl 2xl:leading-[38px]"
      >
        {description}
      </Typography>
      <Spacer size={{ mobileAndAbove: "4x", tabletAndAbove: "3x" }} />
      {token ? (
        <Button
          variant="tertiary"
          className="justify-start!"
          linkTo={brandPage(token)}
        >
          <Typography
            variant="subheadingSansMedium"
            className="text-sm underline lg:text-lg"
          >
            {name}
          </Typography>
        </Button>
      ) : (
        <Typography
          variant="subheadingSansMedium"
          className="text-sm underline lg:text-lg"
        >
          {name}
        </Typography>
      )}
      <Spacer size="1x" />
      <Typography variant="paragraphSansRegular">{location}</Typography>
      <div className="h-8 w-full md:max-h-6 md:min-h-6 md:flex-1 lg:max-h-16 lg:min-h-4" />
      <Button
        variant="secondary"
        style={{ width: "fit-content" }}
        linkTo={cta.link}
        className="hover:bg-core-surface-default! bg-transparent!"
        onClick={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
            "brand_value_banner-cta",
            cta.link,
            "v2"
          );
        }}
      >
        {cta.name}
      </Button>
    </Column>
  );
};
